import { Phone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import BackgroundPattern from "@/components/BackgroundPattern";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const faqs = [
  {
    q: "What is the best roofing company in Albuquerque?",
    a: "Zenith Solutions is consistently rated Albuquerque's top roofing contractor with 150+ five-star reviews, an A+ BBB rating, and official Owens Corning Preferred Contractor status. We provide free inspections, transparent pricing, and industry-leading warranties on every roof we install or repair across the Albuquerque metro area.",
  },
  {
    q: "How much does a new roof cost in Albuquerque NM?",
    a: "A new roof in Albuquerque typically costs between $8,000 and $25,000 depending on the size of your home, roofing material (asphalt shingles, metal, tile, or slate), and roof complexity. Zenith Solutions provides free, no-obligation estimates with fully transparent pricing. We also offer flexible financing options including zero-down plans.",
  },
  {
    q: "What types of roofing materials do you offer?",
    a: "We offer a full range including Owens Corning® Duration and TruDefinition asphalt shingles, standing seam metal roofing, clay and concrete tile, and natural slate. Every material is premium-grade and backed by manufacturer warranties, ideal for New Mexico's desert climate with extreme UV exposure, hail, and monsoon weather.",
  },
  {
    q: "How long does a roof replacement take?",
    a: "A standard roof replacement in Albuquerque takes 1–3 days, while home additions or major remodels may take 4–12 weeks. We provide a detailed timeline upfront and keep you informed at every stage. On-time delivery is core to our promise.",
  },
  {
    q: "Do you help with roof insurance claims after storm damage?",
    a: "Absolutely! Zenith Solutions provides expert insurance claim assistance for hail damage, wind damage, and storm damage roof repairs across Albuquerque and Central New Mexico. We document all damage with photos, work directly with your insurance adjuster, and ensure you receive fair compensation for your claim.",
  },
  {
    q: "What are the benefits of installing solar panels in New Mexico?",
    a: "New Mexico has 300+ days of sunshine, making it one of the best states for solar energy. Solar panels reduce electricity bills by 40–70% monthly. NM offers the Solar Market Development Tax Credit (10% of cost, up to $6,000). Solar also increases your property value by 3-4% and provides energy independence.",
  },
  {
    q: "How much do solar panels cost in Albuquerque?",
    a: "A typical residential solar system in Albuquerque costs $15,000–$30,000 before incentives. After the NM state tax credit (10%, up to $6,000), net cost typically drops to $12,000–$24,000. Most homeowners see full ROI in 7–10 years. Zenith Solutions offers free solar savings analyses and flexible financing.",
  },
  {
    q: "How do I maintain my roof in New Mexico's climate?",
    a: "We recommend annual inspections, especially after monsoon season (July–September) and hail events. Keep gutters clear, trim overhanging branches, check for UV degradation on shingles, and address minor issues promptly. Zenith Solutions offers maintenance plans and free post-installation inspections.",
  },
  {
    q: "Do you offer financing for roofing and solar projects?",
    a: "Yes! We partner with leading lenders to offer flexible financing for roofing, construction, and solar projects. Many Albuquerque homeowners qualify for zero-down options with competitive interest rates. We'll walk you through every option during your free consultation.",
  },
  {
    q: "Are you a licensed and insured contractor in New Mexico?",
    a: "Absolutely. Zenith Solutions is fully licensed in New Mexico (NM CID licensed), bonded, and carries comprehensive general liability and workers' compensation insurance. We maintain an A+ BBB rating, are an Owens Corning Preferred Contractor, and stand behind every project with industry-leading warranties.",
  },
  {
    q: "What areas in New Mexico do you serve?",
    a: "Zenith Solutions serves Albuquerque, Rio Rancho, Corrales, Los Lunas, Bernalillo, Santa Fe, Belen, Edgewood, Moriarty, Placitas, Tijeras, Cedar Crest, Los Ranchos, South Valley, North Valley, Bosque Farms, Peralta, Sandia Park, and all of Central New Mexico within a 50-mile radius.",
  },
];

const FAQSection = () => (
  <section id="faq" className="section-padding bg-section-warm relative">
    <BackgroundPattern variant="roofline" />
    <div className="container mx-auto max-w-3xl relative z-10">
      <div className="text-center mb-12">
        <p className="text-sm font-bold uppercase tracking-widest text-primary mb-3">FAQ</p>
        <h2 className="text-3xl md:text-4xl font-black text-foreground">
          Albuquerque Roofing & Solar Questions Answered
        </h2>
        <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
          Everything you need to know about roofing, solar, and construction services in Albuquerque, New Mexico.
        </p>
      </div>

      <Accordion type="single" collapsible className="space-y-3">
        {faqs.map((f, i) => (
          <AccordionItem key={i} value={`faq-${i}`} className="bg-card border rounded-xl px-6 shadow-sm">
            <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline py-5">
              {f.q}
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed pb-5">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="text-center mt-10">
        <p className="text-muted-foreground mb-4">
          Still have questions about roofing or solar in Albuquerque? We're happy to help.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a href="tel:5056048536">
            <Button
              size="lg"
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold gap-1.5 h-12 px-6"
            >
              <Phone className="h-4 w-4" /> (505) 604-8536
            </Button>
          </a>
          <Link to="/get-estimate">
            <Button
              size="lg"
              variant="outline"
              className="font-bold gap-1.5 border-primary text-primary hover:bg-primary hover:text-primary-foreground h-12 px-6"
            >
              Get Free Estimate <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  </section>
);

export default FAQSection;
